import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import {
  ArrowLeftIcon,
  UserIcon,
  TrashIcon,
  CheckCircleIcon,
  ClockIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { useDiagnosticsStore } from '../stores/diagnosticsStore';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';
import { Modal } from '../components/ui/Modal';
import { ResponseDetailModal } from '../components/diagnostics/ResponseDetailModal';
import { formatRelativeTime } from '../utils/dateFormat';
import clsx from 'clsx';

export function DiagnosticResponsesPage() {
  const { t } = useTranslation('diagnostics');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    selectedForm,
    responses,
    selectedResponse,
    isLoading,
    fetchForm,
    fetchResponses,
    fetchResponse,
    deleteResponse,
    setSelectedResponse,
  } = useDiagnosticsStore();

  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [viewResponseId, setViewResponseId] = useState<string | null>(null);
  const [isLoadingResponse, setIsLoadingResponse] = useState(false);

  const handleViewResponse = async (responseId: string) => {
    if (!id) return;
    setViewResponseId(responseId);
    setIsLoadingResponse(true);
    try {
      await fetchResponse(id, responseId);
    } finally {
      setIsLoadingResponse(false);
    }
  };

  const handleCloseResponseModal = () => {
    setViewResponseId(null);
    setSelectedResponse(null);
  };

  useEffect(() => {
    if (id) {
      fetchForm(id);
      fetchResponses(id);
    }
  }, [id, fetchForm, fetchResponses]);

  const handleDelete = async (responseId: string) => {
    if (!id) return;
    try {
      await deleteResponse(id, responseId);
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting response:', error);
    }
  };

  if (isLoading || !selectedForm) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  const completedResponses = responses.filter(r => r.status === 'completed');
  const inProgressResponses = responses.filter(r => r.status === 'in_progress');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(`/diagnostics/${id}`)}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors flex-shrink-0"
        >
          <ArrowLeftIcon className="w-5 h-5 text-gray-500 dark:text-slate-400" />
        </button>
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
            {t('responses.title')}
          </h1>
          <p className="text-gray-500 dark:text-slate-400 truncate text-sm sm:text-base">{selectedForm.title}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="flex flex-wrap gap-2 sm:gap-4">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 dark:bg-green-900/30 rounded-lg">
          <CheckCircleIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
          <span className="text-sm font-medium text-green-700 dark:text-green-400">
            {completedResponses.length} {t('responses.completed')}
          </span>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
          <ClockIcon className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
          <span className="text-sm font-medium text-yellow-700 dark:text-yellow-400">
            {inProgressResponses.length} {t('responses.inProgress')}
          </span>
        </div>
      </div>

      {/* Responses List */}
      {responses.length === 0 ? (
        <div className="text-center py-12">
          <UserIcon className="w-12 h-12 text-gray-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
            {t('responses.empty')}
          </h3>
          <p className="text-gray-500 dark:text-slate-400">
            {t('responses.emptyDescription')}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {responses.map((response) => (
            <motion.div
              key={response.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => handleViewResponse(response.id)}
              className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 px-4 py-3 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 cursor-pointer hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-sm transition-all"
            >
              <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                {/* Avatar */}
                <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                  <UserIcon className="w-5 h-5 text-gray-500 dark:text-slate-400" />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white truncate">
                    {response.respondentName || t('responses.anonymous')}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-slate-400 truncate">
                    {response.respondentEmail && `${response.respondentEmail} • `}
                    {response.respondentRole && `${response.respondentRole} • `}
                    {response.status === 'completed'
                      ? `${t('responses.completedAt')} ${formatRelativeTime(response.completedAt || response.updatedAt)}`
                      : `${t('responses.startedAt')} ${formatRelativeTime(response.createdAt)}`
                    }
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between sm:justify-end gap-3 pl-13 sm:pl-0">
                {/* Status */}
                <span
                  className={clsx(
                    'px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0',
                    response.status === 'completed'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                  )}
                >
                  {response.status === 'completed' ? t('responses.completed') : t('responses.inProgress')}
                </span>

                {/* Actions */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewResponse(response.id);
                  }}
                  className="p-2 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  title={t('responses.view') || 'Ver respuestas'}
                >
                  <EyeIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteConfirm(response.id);
                  }}
                  className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title={t('responses.delete')}
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-slate-400">
            {t('responses.confirmDelete')}
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
              {t('create.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
            >
              {t('responses.delete')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Response Detail Modal */}
      <ResponseDetailModal
        isOpen={!!viewResponseId}
        onClose={handleCloseResponseModal}
        response={selectedResponse}
        formStructure={selectedForm?.structure || null}
        isLoading={isLoadingResponse}
      />
    </div>
  );
}
